// Single-select teammate picker — search-then-click. Used by Reassign
// dialogs (single-ticket and bulk).
//
// UX: a button that shows the current pick (avatar + name + email). Click
// → dropdown with a search box + a filtered list of active teammates.
// Click a row to select. Type-ahead supported; arrow keys + enter
// optional sugar.
//
// Replaces the older avatar-grid picker, which got cramped with even a
// handful of names. Search-first scales to any team size.

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { useTeamMembers, type TeamMember } from '@/lib/queries';
import { useAuth } from '@/lib/auth-guard';
import { UserAvatar } from '@/components/UserAvatar';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  /** Currently selected teammate's slack_user_id, or null for unselected. */
  value: string | null;
  /** Called with the slack_user_id when a teammate is picked. */
  onChange: (slackUserId: string) => void;
  /** Hide the signed-in user from the list (use on single-ticket reassign:
   *  reassigning to yourself is what Claim does). */
  excludeSelf?: boolean;
  /** Optional sub-heading shown above the picker; defaults to no label. */
  label?: string;
  /** Trigger placeholder when nothing is selected. */
  placeholder?: string;
}

export function AssigneePicker({
  value,
  onChange,
  excludeSelf = false,
  label,
  placeholder = 'Pick a teammate…',
}: Props) {
  const teamQ = useTeamMembers();
  const { teamMember: me } = useAuth();
  const mySlackId = me?.slack_user_id ?? null;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // slack_user_id → TeamMember map for avatar lookups in the trigger.
  const bySlackId = useMemo(() => {
    const m = new Map<string, TeamMember>();
    for (const t of teamQ.data ?? []) m.set(t.slack_user_id, t);
    return m;
  }, [teamQ.data]);

  // Active members only, with self pinned to the top (unless excluded).
  const ordered = useMemo(() => {
    const all = (teamQ.data ?? []).filter((t) => t.active);
    if (excludeSelf) {
      return all
        .filter((t) => t.slack_user_id !== mySlackId)
        .sort((a, b) => a.name.localeCompare(b.name));
    }
    return all.sort((a, b) => {
      if (a.slack_user_id === mySlackId) return -1;
      if (b.slack_user_id === mySlackId) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [teamQ.data, excludeSelf, mySlackId]);

  // Filter against the search query (case-insensitive substring on name +
  // email + slack id).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ordered;
    return ordered.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.email.toLowerCase().includes(q) ||
        t.slack_user_id.toLowerCase().includes(q),
    );
  }, [ordered, query]);

  // Reset active row when filter changes so arrow keys behave predictably.
  useEffect(() => {
    setActiveIdx(0);
  }, [query, open]);

  // Auto-focus the search input on open. The Radix DropdownMenu will trap
  // focus inside the content, but we want focus on the input specifically
  // so users can start typing right away.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 10);
      return () => clearTimeout(t);
    }
  }, [open]);

  const selected = value ? bySlackId.get(value) : null;

  function pick(slackUserId: string) {
    onChange(slackUserId);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (filtered.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % filtered.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + filtered.length) % filtered.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      pick(filtered[activeIdx].slack_user_id);
    }
  }

  return (
    <div className="grid gap-1.5">
      {label ? (
        <p className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</p>
      ) : null}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full cursor-pointer items-center gap-2 rounded-md border border-border/70 bg-background px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent/40',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20',
            )}
          >
            {selected ? (
              <>
                <UserAvatar
                  slackId={selected.slack_user_id}
                  teamMembersBySlackId={bySlackId}
                  size="md"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium leading-tight">{selected.name}</p>
                  <p className="truncate text-2xs text-muted-foreground">{selected.email}</p>
                </div>
              </>
            ) : (
              <span className="flex-1 text-muted-foreground">{placeholder}</span>
            )}
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          // Match the trigger width so the dropdown lines up cleanly.
          className="w-[var(--radix-dropdown-menu-trigger-width)] p-0"
          // Don't auto-focus first item — we want the input to take focus.
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div className="border-b border-border/60 p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search by name or email…"
                className="h-8 pl-7 text-xs"
              />
            </div>
          </div>
          <div className="max-h-[320px] overflow-y-auto py-1">
            {teamQ.isLoading ? (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">Loading…</p>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                {query.trim() ? 'No matches.' : excludeSelf ? 'No other active teammates.' : 'No active teammates.'}
              </p>
            ) : (
              filtered.map((tm, i) => {
                const isMe = tm.slack_user_id === mySlackId;
                const isActive = i === activeIdx;
                const isSelected = value === tm.slack_user_id;
                return (
                  <button
                    key={tm.slack_user_id}
                    type="button"
                    onClick={() => pick(tm.slack_user_id)}
                    onMouseEnter={() => setActiveIdx(i)}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 text-left transition-colors',
                      isActive ? 'bg-accent' : 'hover:bg-accent/50',
                      isSelected && 'bg-accent',
                    )}
                  >
                    <UserAvatar
                      slackId={tm.slack_user_id}
                      teamMembersBySlackId={bySlackId}
                      size="sm"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="truncate text-sm font-medium leading-tight">{tm.name}</p>
                        {isMe ? (
                          <span className="shrink-0 rounded bg-muted px-1 py-0 text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
                            you
                          </span>
                        ) : null}
                      </div>
                      <p className="truncate text-2xs text-muted-foreground">{tm.email}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
